import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '@/context/CartContext';
import SafeIcon from '@/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const CartItem = ({ item }) => {
  const { updateQuantity, removeFromCart } = useCart();
  return (
    <div className="flex gap-4 py-6 border-b border-gray-100">
      <div className="w-20 h-24 bg-gray-50 border border-black overflow-hidden flex-shrink-0">
        <img src={item.image} alt={item.name} className="w-full h-full object-cover grayscale" />
      </div>
      <div className="flex-grow flex flex-col justify-between">
        <div className="flex justify-between items-start">
          <h4 className="font-black text-sm uppercase">{item.name}</h4>
          <button onClick={() => removeFromCart(item.id)} className="text-gray-400 hover:text-black">
            <SafeIcon icon={FiIcons.FiTrash2} />
          </button>
        </div>
        <div className="flex justify-between items-end">
          <div className="flex items-center border border-black">
            <button onClick={() => updateQuantity(item.id, -1)} className="px-2 py-1 hover:bg-gray-100 border-r border-black"><SafeIcon icon={FiIcons.FiMinus} /></button>
            <span className="px-4 text-xs font-black">{item.quantity}</span>
            <button onClick={() => updateQuantity(item.id, 1)} className="px-2 py-1 hover:bg-gray-100 border-l border-black"><SafeIcon icon={FiIcons.FiPlus} /></button>
          </div>
          <span className="font-black text-sm">${item.price * item.quantity}</span>
        </div>
      </div>
    </div>
  );
};

const CartDrawer = () => {
  const { cart, isDrawerOpen, setIsDrawerOpen, total } = useCart();

  return (
    <AnimatePresence>
      {isDrawerOpen && (
        <>
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setIsDrawerOpen(false)}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[70]"
          />
          <motion.div 
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[80] shadow-2xl flex flex-col"
          >
            <div className="p-6 border-b-2 border-black flex justify-between items-center bg-white">
              <h2 className="text-2xl font-black uppercase tracking-tighter">Your Bag ({cart.length})</h2>
              <button onClick={() => setIsDrawerOpen(false)} className="p-2 hover:rotate-90 transition-transform">
                <SafeIcon icon={FiIcons.FiX} className="text-2xl" />
              </button>
            </div>

            <div className="flex-grow overflow-y-auto px-6 no-scrollbar">
              {cart.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center">
                  <SafeIcon icon={FiIcons.FiShoppingBag} className="text-6xl text-gray-100 mb-4" />
                  <p className="font-black uppercase tracking-widest text-gray-400">Empty Workspace</p>
                  <button onClick={() => setIsDrawerOpen(false)} className="mt-6 text-xs font-black underline">CONTINUE EXPLORING</button>
                </div>
              ) : (
                cart.map(item => <CartItem key={item.id} item={item} />)
              )}
            </div>

            {cart.length > 0 && (
              <div className="p-6 border-t-2 border-black bg-gray-50">
                <div className="flex justify-between mb-6">
                  <span className="font-bold text-gray-500 uppercase tracking-widest text-xs">Subtotal</span>
                  <span className="font-black text-xl">${total}</span>
                </div>
                <button className="w-full py-5 bg-black text-white font-black tracking-[0.2em] uppercase hover:prism-gradient transition-all">
                  Proceed to Checkout
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;